/**
 * Calendar-style monthly heatmap.
 *
 * Layout: 7 columns × N rows (one row per week). The grid is rendered
 * RTL so the first day of the month lands on the right side of its
 * row, and weeks fill right-to-left then top-to-bottom — matching the
 * Statuspage-style reference design.
 *
 * Past days without probe data and future days both render as muted
 * "empty" cells so the grid keeps its rectangular shape.
 */

import type { DailyUptime } from "../api/status";

interface Props {
  /** First day of the month (UTC) to render. */
  month: Date;
  /** Daily uptime samples — extra dates are ignored, missing days become empty cells. */
  days: DailyUptime[];
}

interface CellStyle {
  /** Inline color so we don't fight Tailwind purging on dynamic values. */
  background: string;
  borderColor?: string;
  tooltip: string;
  isFuture: boolean;
  isEmpty: boolean;
}

const COLORS = {
  great: "#166534", // ≥99.99%
  good: "#4ade80", // ≥99.9%
  ok: "#86efac", // ≥99%
  warn: "#ca8a04", // ≥95%
  bad: "#ef4444", // <95%
  empty: "rgba(255,255,255,0.04)",
  emptyBorder: "rgba(255,255,255,0.08)",
};

function classify(
  d: DailyUptime | undefined,
  dateKey: string,
  todayKey: string,
): CellStyle {
  const isFuture = dateKey > todayKey;
  if (isFuture || !d || d.totalProbes === 0) {
    return {
      background: COLORS.empty,
      borderColor: COLORS.emptyBorder,
      tooltip: isFuture ? `${dateKey} · upcoming` : `${dateKey} · No data`,
      isFuture,
      isEmpty: true,
    };
  }
  let background = COLORS.good;
  if (d.uptimePct >= 99.99) background = COLORS.great;
  else if (d.uptimePct >= 99.9) background = COLORS.good;
  else if (d.uptimePct >= 99) background = COLORS.ok;
  else if (d.uptimePct >= 95) background = COLORS.warn;
  else background = COLORS.bad;
  return {
    background,
    tooltip: `${dateKey} · ${d.uptimePct.toFixed(2)}% (${d.okProbes}/${d.totalProbes})`,
    isFuture: false,
    isEmpty: false,
  };
}

function ymd(d: Date): string {
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
  )
    .toISOString()
    .slice(0, 10);
}

function todayKey(): string {
  return ymd(new Date());
}

export function MonthlyHeatmap({ month, days }: Props): JSX.Element {
  const year = month.getUTCFullYear();
  const monthIdx = month.getUTCMonth();
  const byDate = new Map(days.map((d) => [d.date, d]));

  const firstOfMonth = new Date(Date.UTC(year, monthIdx, 1));
  const daysInMonth = new Date(Date.UTC(year, monthIdx + 1, 0)).getUTCDate();
  const lastOfMonth = new Date(Date.UTC(year, monthIdx, daysInMonth));
  // Sunday-first weekday index (0 = Sun, 6 = Sat).
  const leadSpacers = firstOfMonth.getUTCDay();
  const trailSpacers = 6 - lastOfMonth.getUTCDay();

  type Cell =
    | { kind: "spacer"; key: string }
    | { kind: "day"; key: string; date: string; day: DailyUptime | undefined };

  const cells: Cell[] = [];
  for (let i = 0; i < leadSpacers; i++) {
    cells.push({ kind: "spacer", key: `lead-${i}` });
  }
  for (let day = 1; day <= daysInMonth; day++) {
    const key = ymd(new Date(Date.UTC(year, monthIdx, day)));
    cells.push({ kind: "day", key, date: key, day: byDate.get(key) });
  }
  for (let i = 0; i < trailSpacers; i++) {
    cells.push({ kind: "spacer", key: `trail-${i}` });
  }

  // Month uptime % — weighted by probes; excludes future and no-data days.
  const totals = days.reduce(
    (acc, d) => ({ total: acc.total + d.totalProbes, ok: acc.ok + d.okProbes }),
    { total: 0, ok: 0 },
  );
  const monthPct = totals.total === 0 ? null : (totals.ok / totals.total) * 100;

  const monthLabel = month.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
  const today = todayKey();

  return (
    <div>
      <header className="mb-4 flex items-baseline justify-between">
        <h3 className="text-lg font-semibold text-dr-heading">{monthLabel}</h3>
        <span
          className="text-xl font-bold tabular-nums"
          style={{ color: monthPct !== null ? "#15803d" : undefined }}
        >
          {monthPct === null ? "—" : `${monthPct.toFixed(2)}%`}
        </span>
      </header>
      <div
        className="grid"
        style={{
          gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          gap: "6px",
          direction: "rtl", // first day of month renders top-right, flows leftward
        }}
        role="grid"
        aria-label={`Uptime for ${monthLabel}`}
      >
        {cells.map((c) => {
          if (c.kind === "spacer") {
            return <div key={c.key} aria-hidden className="aspect-square" />;
          }
          const style = classify(c.day, c.date, today);
          return (
            <div
              key={c.key}
              role="gridcell"
              title={style.tooltip}
              className="aspect-square rounded-[8px] border transition-transform hover:scale-[1.08]"
              style={{
                background: style.background,
                borderColor: style.borderColor ?? "transparent",
                // Smaller cells on mobile, capped on desktop so a 3-month
                // side-by-side layout fits comfortably.
                minWidth: "26px",
                maxWidth: "36px",
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
